import * as XLSX from 'xlsx';
import type { TimeEntry } from '@altyor-kronos/shared-types';

export interface DetailedReportMaps {
  projectsById: Map<string, { code: string; name: string; clientId: string }>;
  clientNameById: Map<string, string>;
  userDisplayNameById: Map<string, string>;
  userGroupById: Map<string, string>;
  designationNameById: Map<string, string>;
}

export interface DetailedReportRow {
  project: string;
  client: string;
  description: string;
  task: string;
  user: string;
  group: string;
  tags: string;
  startDate: Date;
  endDate: Date;
  durationH: string;
  durationDecimal: number;
  dateOfCreation: Date;
  mois: number;
  annee: number;
}

/** durationSeconds → "h:mm" (e.g. 27900 → "7:45", 0 → "0:00"). */
function formatHMM(seconds: number): string {
  const totalMin = Math.max(0, Math.round(seconds / 60));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}:${m.toString().padStart(2, '0')}`;
}

export function buildDetailedReportRows(
  entries: TimeEntry[],
  maps: DetailedReportMaps,
): DetailedReportRow[] {
  const rows = entries.map((e): DetailedReportRow => {
    const p = maps.projectsById.get(e.projectId);
    const start = e.startTime.toDate();
    return {
      project: p ? `${p.code} - ${p.name}` : e.projectId,
      client: p ? (maps.clientNameById.get(p.clientId) ?? '') : '',
      description: e.description ?? '',
      task: maps.designationNameById.get(e.designationId) ?? '',
      user: maps.userDisplayNameById.get(e.userId) ?? e.userId,
      group: maps.userGroupById.get(e.userId) ?? '',
      tags: (e.tags ?? []).join(', '),
      startDate: start,
      endDate: e.endTime.toDate(),
      durationH: formatHMM(e.durationSeconds),
      durationDecimal: e.durationSeconds / 3600,
      dateOfCreation: e.createdAt.toDate(),
      mois: start.getMonth() + 1,
      annee: start.getFullYear(),
    };
  });
  rows.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  return rows;
}

export const DETAILED_REPORT_HEADERS = [
  'Project',
  'Client',
  'Description',
  'Task',
  'User',
  'Group',
  'Tags',
  'Start Date',
  'End Date',
  'Duration (h)',
  'Duration (decimal)',
  'Date of creation',
  'Mois',
  'Année',
] as const;

export function buildDetailedReportWorkbook(rows: DetailedReportRow[]): XLSX.WorkBook {
  const aoa: unknown[][] = [
    [...DETAILED_REPORT_HEADERS],
    ...rows.map((r) => [
      r.project,
      r.client,
      r.description,
      r.task,
      r.user,
      r.group,
      r.tags,
      r.startDate,
      r.endDate,
      r.durationH,
      r.durationDecimal,
      r.dateOfCreation,
      r.mois,
      r.annee,
    ]),
  ];
  // cellDates: true makes Date values serialize as Excel date cells (type 'd'),
  // matching the Clockify export where these columns are date serials.
  const ws = XLSX.utils.aoa_to_sheet(aoa, { cellDates: true });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Detailed Report');
  return wb;
}
